import { useState, useRef, useEffect, useLayoutEffect, useCallback } from "react"

export interface PopoverStyle {
  position: "fixed"
  top: number
  left: number
  minWidth: number
  maxHeight: number
}

/**
 * Anchored dropdown state for the filter/select popovers. The popover is meant
 * to be rendered in a portal to `document.body` (see SelectMenu/MultiSelectFilter)
 * so it escapes the table card's `overflow-hidden` — an absolutely-positioned
 * popover inside that card is clipped when the table is short, and no z-index can
 * un-clip it. Positioning is therefore `fixed`, recomputed from the trigger's
 * bounding rect on open, scroll, and resize, and flipped above the trigger when
 * there is more room there.
 *
 * Because the popover lives outside the trigger in the DOM, outside-click
 * detection checks BOTH the trigger and the popover refs.
 */
export function usePopover<T extends HTMLElement = HTMLDivElement>() {
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<T>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const [popoverStyle, setPopoverStyle] = useState<PopoverStyle | null>(null)

  const reposition = useCallback(() => {
    const el = triggerRef.current
    if (!el) return
    const r = el.getBoundingClientRect()
    const margin = 4
    const cap = 280
    const spaceBelow = window.innerHeight - r.bottom - margin
    const spaceAbove = r.top - margin
    // Flip up only when below is genuinely cramped and above is roomier.
    const openUp = spaceBelow < 200 && spaceAbove > spaceBelow
    const maxHeight = Math.max(120, Math.min(cap, openUp ? spaceAbove : spaceBelow))
    const top = openUp ? Math.max(margin, r.top - margin - maxHeight) : r.bottom + margin
    setPopoverStyle({ position: "fixed", top, left: r.left, minWidth: r.width, maxHeight })
  }, [])

  // Position before paint so the popover never flashes at (0,0), and keep it
  // glued to the trigger while the page scrolls or the window resizes.
  useLayoutEffect(() => {
    if (!open) {
      setPopoverStyle(null)
      return
    }
    reposition()
    const onScroll = () => reposition()
    window.addEventListener("scroll", onScroll, true)
    window.addEventListener("resize", reposition)
    return () => {
      window.removeEventListener("scroll", onScroll, true)
      window.removeEventListener("resize", reposition)
    }
  }, [open, reposition])

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      const t = e.target as Node
      if (triggerRef.current?.contains(t)) return
      if (popoverRef.current?.contains(t)) return
      setOpen(false)
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false)
    }
    document.addEventListener("mousedown", handler)
    document.addEventListener("keydown", onKeyDown)
    return () => {
      document.removeEventListener("mousedown", handler)
      document.removeEventListener("keydown", onKeyDown)
    }
  }, [open])

  return { open, setOpen, triggerRef, popoverRef, popoverStyle }
}
